import { useTranslation } from 'react-i18next';

export type FlagLevel = 'red' | 'yellow' | 'green' | 'none';
export type TeacherFlagLevel = 'red' | 'yellow' | 'green';
export type FlagCategory = 'attendance' | 'homework' | 'community';

export const FLAG_CATEGORIES: FlagCategory[] = ['attendance', 'homework', 'community'];

export type AutoFlagReason = { code: string; category: FlagCategory; value: number; threshold?: number };
export type CategoryFlagData = { level: FlagLevel; reasons: AutoFlagReason[] };
export type AutoFlagData = {
  level: FlagLevel;
  reasons: AutoFlagReason[];
  categories: Record<FlagCategory, CategoryFlagData>;
};

export type StudentMetricsData = {
  since: Date | string;
  windowDays: number;
  presence: { rate: number | null; present: number; total: number };
  homework: { rate: number | null; done: number; total: number };
  engagement: { rate: number | null; done: number; total: number };
  community: { rate: number | null; done: number; total: number };
};

export type TeacherFlagData = {
  studentFlagId: string;
  flag: TeacherFlagLevel;
  reason: string | null;
  comment: string;
  createdAt: Date | string;
  teacherName: string;
};

export const FLAG_STYLES: Record<FlagLevel, { pill: string; dot: string; ring: string }> = {
  red: { pill: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500', ring: 'border-rose-400 ring-rose-200' },
  yellow: { pill: 'bg-amber-100 text-amber-800', dot: 'bg-amber-400', ring: 'border-amber-400 ring-amber-200' },
  green: { pill: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', ring: 'border-emerald-400 ring-emerald-200' },
  none: { pill: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', ring: 'border-gray-300 ring-gray-200' },
};

/** Weeks covered by the risk window, for the "{{weeks}}" placeholders. */
export function metricsWeeks(metrics: { windowDays: number }) {
  return Math.round(metrics.windowDays / 7);
}

/** Translated reason lines for an automatic flag (empty for level "none"). */
export function useAutoFlagReasonLines() {
  const { t } = useTranslation('admin');
  return (autoFlag: { reasons: AutoFlagReason[] }) =>
    autoFlag.reasons.map((reason) =>
      t(`teacher.flags.reasons.${reason.code}`, {
        value: reason.value,
        threshold: reason.threshold,
      }),
    );
}

export function FlagPill({
  level,
  label,
  title,
  className,
}: {
  level: FlagLevel;
  label?: string;
  title?: string;
  className?: string;
}) {
  const { t } = useTranslation('admin');
  const style = FLAG_STYLES[level];
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${style.pill} ${className ?? ''}`}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${style.dot}`} />
      {label ?? t(`teacher.flags.levels.${level}`)}
    </span>
  );
}

/**
 * The three category pills ("Frequência: Red flag", ...). Categories without
 * an alert are shown in gray so the row always has the same shape.
 */
export function CategoryPills({ autoFlag, className }: { autoFlag: AutoFlagData; className?: string }) {
  const { t } = useTranslation('admin');
  const reasonLines = useAutoFlagReasonLines();
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className ?? ''}`}>
      {FLAG_CATEGORIES.map((category) => {
        const entry = autoFlag.categories[category];
        const level = entry?.level ?? 'none';
        const style = FLAG_STYLES[level];
        const title = entry ? reasonLines(entry).join('; ') : '';
        return (
          <span
            key={category}
            title={title || undefined}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${style.pill} ${
              level === 'none' ? 'opacity-70' : ''
            }`}
          >
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`} />
            {t('teacher.flags.categoryFlag', {
              category: t(`teacher.flags.categories.${category}`),
              level: t(`teacher.flags.levels.${level}`),
            })}
          </span>
        );
      })}
    </div>
  );
}

export function AutoFlagReasons({ autoFlag }: { autoFlag: AutoFlagData }) {
  const { t } = useTranslation('admin');
  const reasonLines = useAutoFlagReasonLines();
  const lines = reasonLines(autoFlag);

  if (autoFlag.level === 'none' || lines.length === 0) {
    return <p className="text-sm text-gray-500">{t('teacher.flags.noneHint')}</p>;
  }

  return (
    <ul className="space-y-1 text-sm text-gray-700">
      {lines.map((line, index) => {
        const reason = autoFlag.reasons[index];
        const level = reason ? autoFlag.categories[reason.category]?.level ?? autoFlag.level : autoFlag.level;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`${reason?.code ?? 'reason'}-${index}`} className="flex items-start gap-2">
            <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${FLAG_STYLES[level].dot}`} />
            <span>{line}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function MetricsGrid({ metrics }: { metrics: StudentMetricsData }) {
  const { t } = useTranslation('admin');
  const tiles: Array<{ key: string; label: string; rate: number | null; done: number; total: number }> = [
    {
      key: 'presence',
      label: t('teacher.flags.metrics.presence'),
      rate: metrics.presence.rate,
      done: metrics.presence.present,
      total: metrics.presence.total,
    },
    {
      key: 'homework',
      label: t('teacher.flags.metrics.homework'),
      rate: metrics.homework.rate,
      done: metrics.homework.done,
      total: metrics.homework.total,
    },
    {
      key: 'engagement',
      label: t('teacher.flags.metrics.engagement'),
      rate: metrics.engagement.rate,
      done: metrics.engagement.done,
      total: metrics.engagement.total,
    },
    {
      key: 'community',
      label: t('teacher.flags.metrics.community'),
      rate: metrics.community.rate,
      done: metrics.community.done,
      total: metrics.community.total,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {tiles.map((tile) => (
        <div key={tile.key} className="rounded-xl bg-[#fff9fb] px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{tile.label}</p>
          {tile.rate !== null ? (
            <>
              <p className="text-lg font-semibold text-gray-900">{tile.rate}%</p>
              <p className="text-xs text-gray-500">
                {t('teacher.flags.metrics.ratio', { done: tile.done, total: tile.total })}
              </p>
            </>
          ) : (
            <p className="pt-1 text-xs text-gray-500">{t('teacher.flags.metrics.noData')}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/** The teacher's own flag: pill, reason category, comment and who set it. */
export function TeacherFlagSummary({
  flag,
  showManualLabel = true,
  clampComment = false,
}: {
  flag: TeacherFlagData;
  showManualLabel?: boolean;
  clampComment?: boolean;
}) {
  const { t } = useTranslation('admin');
  const label = showManualLabel
    ? `${t('teacher.flags.manual')} · ${t(`teacher.flags.levels.${flag.flag}`)}`
    : undefined;
  const reasonLabel = flag.reason ? t(`teacher.flags.reasonCategories.${flag.reason}`) : t('teacher.flags.noReason');
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <FlagPill level={flag.flag} label={label} />
        <span className="text-xs font-medium text-gray-700">{reasonLabel}</span>
      </div>
      <p className={`text-sm italic text-gray-600 ${clampComment ? 'line-clamp-2' : 'whitespace-pre-line'}`}>
        {flag.comment}
      </p>
      <p className="text-xs text-gray-400">
        {t('teacher.flags.setBy', {
          name: flag.teacherName,
          date: new Date(flag.createdAt).toLocaleDateString(),
        })}
      </p>
    </div>
  );
}
